import { useEffect, useRef, useState } from 'react';
import { User, UserPlan } from '@/models';
import { API_BASE } from '@/config.ts';
import { useNavigate } from 'react-router';

type Props = {
  plan: UserPlan | null;
  user: User | null;
};

const MIN_DELAY = 7 * 60 * 1000; // 7 min
const MAX_DELAY = 15 * 60 * 1000; // 15 min
// testing:
// const MIN_DELAY = 10 * 1000;
// const MAX_DELAY = 20 * 1000;
const JINGLES = [
  '/assets/audio/cyberkoala-calls-rocket-left.wav',
  '/assets/audio/jingle-4.wav',
  '/assets/audio/push-rocket-left-1.wav',
  '/assets/audio/push-rocket-left-2.wav',
  '/assets/audio/jingle-6.wav',
  '/assets/audio/jingle-7.wav',
  '/assets/audio/jingle-8.wav',
  // '/assets/audio/jingle-9.wav',
  // '/assets/audio/jingle-11.wav',
];

// TODO request: server-wise ads, if server admin has OPTIMUM/EXTENDED plan -> dont play ads this server members
export function AudioAds({ plan, user }: Props) {
  const adRef = useRef<HTMLAudioElement | null>(null);
  const jingleRef = useRef<HTMLAudioElement | null>(null);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const navigate = useNavigate();
  const [isAdVisible, setIsAdVisible] = useState(false);
  const [currentTrack, setCurrentTrack] = useState<'ad1' | 'ad2'>('ad1');
  const [progress, setProgress] = useState(0);

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const [resolvedPlan, setResolvedPlan] = useState<UserPlan | null>(plan);

  const randomJingle = () => JINGLES[Math.floor(Math.random() * JINGLES.length)];

  // TODO: compare with deletedUsers table. If user deleted with same email/tel use date of deletion, or not use this logic (show always ads)
  const isOlderThanOneMonth = () => {
    if (!user?.createdAt) return false;

    const created = new Date(user.createdAt);

    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

    return created <= oneMonthAgo;
  };

  const isOlderThanThreeMonth = () => {
    if (!user?.createdAt) return false;

    const created = new Date(user.createdAt);

    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 3);

    return created <= oneMonthAgo;
  };

  const formatTime = (sec: number) => {
    if (!sec || Number.isNaN(sec)) return '0:00';

    const minutes = Math.floor(sec / 60);
    const seconds = Math.floor(sec % 60);

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const startProgressTracking = (audio: HTMLAudioElement) => {
    stopProgressTracking();

    setProgress(0);

    progressIntervalRef.current = setInterval(() => {
      if (!audio.duration) return;

      setCurrentTime(audio.currentTime);
      setDuration(audio.duration);

      const value = (audio.currentTime / audio.duration) * 100;

      setProgress(value);

      if (audio.ended) {
        setProgress(100);
      }
    }, 250);
  };

  const stopProgressTracking = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  useEffect(() => {
    if (plan != null) {
      setResolvedPlan(plan);
    }
  }, [plan]);

  useEffect(() => {
    if (plan !== null) return;
    if (!user?.id) return;

    // USER TOO NEW → NEVER LOAD ADS
    if (!isOlderThanOneMonth()) return;

    // ALSO show ad time (current and total in card)
    let mounted = true;

    const loadPlan = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/users/${user.id}`);

        if (!res.ok) return;

        const data = await res.json();

        if (mounted && data?.plan) {
          setResolvedPlan(data.plan);
        }
      } catch (e) {
        console.error('Failed loading plan:', e);
      }
    };

    loadPlan();

    return () => {
      mounted = false;
    };
  }, [plan, user]);

  // random play interval
  useEffect(() => {
    const clear = () => {
      timeoutRef.current && clearTimeout(timeoutRef.current);

      [jingleRef.current, adRef.current].forEach((audio) => {
        if (!audio) return;

        audio.pause();
        audio.currentTime = 0;
      });
    };

    // NOT FREE OR REGISTERED <3 MONTH
    if (resolvedPlan == null || resolvedPlan !== UserPlan.FREE || !isOlderThanOneMonth()) {
      clear();

      return;
    }

    const getRandomDelay = () => Math.floor(Math.random() * (MAX_DELAY - MIN_DELAY) + MIN_DELAY);

    const waitForAudio = (audio: HTMLAudioElement) =>
      new Promise<void>((resolve, reject) => {
        const onEnded = () => {
          audio.removeEventListener('ended', onEnded);
          resolve();
        };

        audio.addEventListener('ended', onEnded);

        audio.play().catch(reject);
      });

    const playAudioSequence = async () => {
      try {
        setIsAdVisible(true);

        // RANDOM MUSIC JINGLE
        if (jingleRef.current) {
          const audio = jingleRef.current;

          audio.pause();

          audio.src = randomJingle();
          audio.load();

          setCurrentTrack('ad1');

          audio.currentTime = 0;
          audio.volume = 0.9;

          startProgressTracking(audio);

          await waitForAudio(audio);
        }

        // MAIN VOICE AD
        if (adRef.current) {
          const audio = adRef.current;

          setCurrentTrack('ad2');

          audio.currentTime = 0;
          audio.volume = 0.8;

          startProgressTracking(audio);

          await waitForAudio(audio);
        }
      } catch (e) {
        console.warn(e);
      } finally {
        stopProgressTracking();
        setProgress(0);

        setCurrentTime(0);
        setDuration(0);

        setIsAdVisible(false);
      }
    };

    const scheduleNext = () => {
      timeoutRef.current = setTimeout(async () => {
        await playAudioSequence();
        scheduleNext();
      }, getRandomDelay());
    };

    scheduleNext();

    return clear;
  }, [resolvedPlan]);

  if (resolvedPlan !== UserPlan.FREE || !isOlderThanOneMonth()) {
    return null;
  }

  const title = currentTrack === 'ad1' ? 'Интеграция 1' : 'Интеграция 2';

  const subtitle = currentTrack === 'ad1' ? 'Музыкальная пауза' : 'Ознакомительный тариф ZERO';

  // TODO: add skip button to navigate to boost (skip add onClick={() => navigate('/channels/@me/boost')})
  return (
    <>
      <audio ref={jingleRef} preload="auto" />

      <audio ref={adRef} preload="auto" src="/assets/audio/calls-ad3.wav" />

      {isAdVisible && (
        <div className="audio-ad-modal">
          <div className="audio-ad-content">
            <img src="/assets/ads/rocket-ad-2.png" className="audio-ad-cover" alt="logo" />

            <div className="audio-ad-meta">
              <div className="audio-ad-title">{title}</div>

              <div className="audio-ad-subtitle">{subtitle}</div>

              <div className="text-muted-foreground text-sm">
                {formatTime(currentTime)} / {formatTime(duration)}
              </div>

              <div className="audio-ad-progress">
                <div
                  className="audio-ad-progress-fill"
                  style={{
                    width: `${progress}%`,
                  }}
                />
              </div>
              {progress > 20 && (
                <button
                  onClick={() => navigate('/channels/@me/boost')}
                  className="mt-4 px-4 py-2 rounded-md bg-zinc-800 hover:bg-zinc-700 text-white text-sm transition"
                >
                  Отключить рекламу
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
